<template>
    <vx-table-row data-test="segment-row">
        <vx-table-row-title class="md:col-span-full! lg:col-auto!">
            <div
                v-if="corporateSegment && !props.templateView"
                class="mb-1 text-xs text-slate-700 font-normal flex items-center gap-1"
            >
                <font-awesome-icon widthAuto :icon="faCrown" class="text-base text-amber-400"></font-awesome-icon>
                <span> Corp Segment </span>
            </div>
            <div class="flex items-center gap-2">
                <div class="break-words max-w-md">{{ segment.name }}</div>
                <segment-description-popover :modelValue="props.segment"></segment-description-popover>
            </div>
        </vx-table-row-title>

        <vx-table-row-item v-if="!props.templateView">
            <template v-slot:label> Contacts: </template>
            <font-awesome-icon
                widthAuto
                v-if="contactsCount[props.segment.id] == undefined"
                :icon="faLoader"
                spin
            ></font-awesome-icon>

            <vx-popover
                v-else
                arrow
                hover
                placement="top"
                class="cursor-pointer"
                @click="copyText(contactsCount[props.segment.id])"
            >
                <span data-test="contacts-count">
                    {{ formatCount(contactsCount[props.segment.id]) }}
                </span>
                <template v-slot:content>
                    <div class="text-center">
                        {{ Intl.NumberFormat('en-US').format(contactsCount[props.segment.id]) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Audience Type: </template>
            {{ segmentAudienceType }}
        </vx-table-row-item>

        <vx-table-row-item v-if="props.templateView" data-test="groups-count">
            <template v-slot:label> Groups: </template>
            <font-awesome-icon
                widthAuto
                v-if="groupCounts[props.segment.id] == undefined"
                :icon="faLoader"
                spin
            ></font-awesome-icon>
            <template v-else>
                {{ groupCounts[props.segment.id] }}
            </template>
        </vx-table-row-item>

        <vx-table-row-item v-if="props.templateView">
            <template v-slot:label> Updated: </template>
            <span
                :class="dateTextColor(props.segment.updated_at)"
                :title="shortDateTime(props.segment.updated_at)"
                :key="refreshKey"
                >{{ fromNow(props.segment.updated_at) }}
            </span>
        </vx-table-row-item>

        <vx-table-row-item v-if="props.templateView || props.groupId">
            <template v-slot:label> Preferred Group: </template>
            <div class="flex items-center gap-2 text-slate-500">
                <font-awesome-icon fixedWidth class="text-base" :icon="segment.preferred_group ? faStar : faStarReg" />
                {{ segment.preferred_group ? 'Pref' : 'All' }}
            </div>
        </vx-table-row-item>
        <vx-table-row-item v-if="!props.templateView && !props.groupId && !props.orgUnitId">
            <template v-slot:label> Refresh Type: </template>
            <div class="whitespace-nowrap">
                <font-awesome-icon fixedWidth :icon="segment.type === 'static' ? faMapPin : faLoader" />
                {{ segmentRefreshType }}
            </div>
        </vx-table-row-item>

        <vx-table-row-item v-if="!props.templateView && !props.groupId && !props.orgUnitId">
            <template v-slot:label> Last Refreshed: </template>

            <vx-popover v-if="corporateSegment" hover arrow placement="top">
                <span class="cursor-help">-</span>
                <template v-slot:content> Contacts are calculated at send time for corporate segments. </template>
            </vx-popover>
            <span v-else :class="lastRefreshedColor" data-test="segment-last-refreshed">
                <font-awesome-icon
                    widthAuto
                    v-if="loading || segmentStatus === null"
                    :icon="faLoader"
                    spin
                ></font-awesome-icon>
                <template v-else>
                    <span v-if="isStatic">
                        <template v-if="segmentStatus === 'processing'"> Processing </template>
                        <template v-else>
                            <span :title="shortDateTime(lastRefreshedAt)" :key="refreshKey"
                                >{{ fromNow(lastRefreshedAt) }}
                            </span>
                        </template>
                    </span>
                    <span v-else>
                        {{ segmentStatus === 'processing' ? 'Processing' : 'Now' }}
                    </span>
                </template>
            </span>
        </vx-table-row-item>

        <vx-table-row-item v-if="showFlow">
            <template v-slot:label> Flow: </template>
            <vx-popover
                v-if="props.segment.flow_id"
                hover
                :arrow="true"
                placement="top"
                @mouseenter="loadFlowOnHover"
                data-test="flow-popover"
            >
                <font-awesome-icon widthAuto :icon="faSlidersSimple"></font-awesome-icon>
                <template v-slot:content>
                    <span v-if="flow" class="text-sm font-normal">{{ flow.name }}</span>
                    <font-awesome-icon v-else widthAuto :icon="faLoader" spin></font-awesome-icon>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="!props.readOnly && !corporateSegment">
            <segment-row-dropdown :segment="props.segment" @deleted="emit('deleted', $event)"></segment-row-dropdown>
            <vx-button
                data-test="edit-button"
                :href="
                    props.templateView
                        ? `/contacts/segments/templates/${props.segment.id}`
                        : `/contacts/segments/${props.segment.id}`
                "
                color="muted"
                outline
            >
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faCrown, faFilePen, faLoader, faMapPin, faStar } from '@fortawesome/pro-solid-svg-icons';
import { faSlidersSimple, faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
    VxTableRowButtons,
    VxPopover,
} from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { audienceTypeLabel } from '../../../constants/campaign';
import { SegmentType, SegmentTypeToLabel } from '../../../constants/segment';
import { segmentsClient } from '~/services';
import { useResourcesFlow } from '~/composables/useResourcesFlow.js';
import { fromNow, shortDateTime, dateTextColor } from '../../../utils/date';
import { formatCount } from '../../filters';
import SegmentRowDropdown from './SegmentRowDropdown.vue';
import { useInterval } from '@vueuse/core';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { useSegmentsGroupsCount } from '~/composables/useSegmentsGroupsCount';
import { useCopyText } from '~/composables/useCopyText';
import SegmentDescriptionPopover from '~/components/contacts/segments/SegmentDescriptionPopover.vue';

const FEATURES = inject('FEATURES');

const props = defineProps({
    segment: {
        type: Object,
        required: true,
    },
    // org unit id and group id that is visualizing the view, not the org unit id or group id of the segment.
    // a segment row can be viewed by an org unit or group and have its org_unit_id or group_id null
    // when it was created by a corporate and is a template
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});

const corporateSegment = computed(() => props.segment.is_template && (props.groupId || props.orgUnitId));

const emit = defineEmits(['deleted']);

const loading = ref(false);

const { contactsCount } = useSegmentsContactsCount();
const { groupCounts } = useSegmentsGroupsCount();
const segmentStatus = ref(null);

const lastRefreshedAt = ref(null);

const { copyText } = useCopyText();

const { loadFlow, flows } = useResourcesFlow();
const showFlow = computed(() => FEATURES.flows && !props.templateView && !props.groupId && !props.orgUnitId);
const flow = computed(() =>
    showFlow.value && props.segment.flow_id && flows.value.has(props.segment.flow_id)
        ? flows.value.get(props.segment.flow_id)
        : undefined
);

const lastRefreshedColor = computed(() => {
    if (!lastRefreshedAt.value) {
        return null;
    }

    if (segmentStatus.value === 'complete' && props.segment.type === SegmentType.AUTO_REFRESH) {
        return 'text-emerald-700';
    }

    const diffInDays = dayjs().diff(lastRefreshedAt.value, 'days');

    if (diffInDays <= 1) {
        return 'text-emerald-700';
    }

    if (diffInDays <= 5) {
        return 'text-amber-600';
    }

    return 'text-rose-600';
});

const isStatic = computed(() => props.segment.type === SegmentType.STATIC);

const segmentRefreshType = computed(() => SegmentTypeToLabel[props.segment.type]);

const segmentAudienceType = computed(() => audienceTypeLabel(props.segment.audience_type));

// used when Laravel Echo updates in real time
watch(
    () => props.segment.contacts_count,
    (newCount) => {
        if (newCount === undefined) {
            return;
        }
        contactsCount.value[props.segment.id] = newCount;
    },
    {
        immediate: true,
    }
);

watch(
    () => props.segment.last_refreshed_at,
    (newLastRefreshedAt) => {
        lastRefreshedAt.value = newLastRefreshedAt;
        segmentStatus.value = !lastRefreshedAt.value ? 'processing' : 'complete';
    },
    {
        immediate: true,
    }
);

const loadData = async () => {
    if (corporateSegment.value || props.templateView || props.groupId || props.orgUnitId) {
        return;
    }

    try {
        loading.value = true;

        const segmentStatusData = await segmentsClient.getSegmentStatus(
            { teamId: props.segment.team_id },
            props.segment.id
        );
        lastRefreshedAt.value = segmentStatusData.data.last_refreshed_at;
        segmentStatus.value = segmentStatusData.data.status;
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const loadFlowOnHover = () => {
    if (flow.value === undefined) {
        loadFlow(props.segment.team_id, props.segment.flow_id);
    }
};

const refreshKey = useInterval(60000);

onMounted(() => {
    loadData();
});
</script>
